import { Action, createReducer, on } from '@ngrx/store';
import {
    initInputFieldProperties,
    inputFieldPropertiesFailed,
    inputFieldPropertiesLoaded,
    inputFieldPropertiesLoadedFromStorage,
} from '../actions/input-field-properties.actions';
import {
    initialInputFieldPropertiesCollectionState,
    InputFieldPropertiesCollectionState,
    InputFieldPropertyState,
} from '../input-field-properties-collection.state';
import { LoadStatus } from '../../load-status';

/**
 * Marks the slice as loading without discarding the properties already in it.
 *
 * @param state
 */
export function setInputFieldPropertiesLoadingReducer(
    state: Readonly<InputFieldPropertiesCollectionState>,
): Readonly<InputFieldPropertiesCollectionState> {
    const status = state.status === LoadStatus.Loaded ? LoadStatus.Loaded : LoadStatus.Loading;

    return { ...state, status };
}

/**
 * Replaces the full property collection — this is bulk reference data, so a load never
 * merges into what is already there.
 *
 * @param state the input-field-properties slice
 * @param props the action payload
 * @param props.properties the properties returned by the load
 * @returns the slice holding the new properties and marked as loaded
 */
export function setInputFieldPropertiesLoadedReducer(
    state: Readonly<InputFieldPropertiesCollectionState>,
    props: { properties: InputFieldPropertyState[] },
): Readonly<InputFieldPropertiesCollectionState> {
    return { ...state, properties: props.properties, status: LoadStatus.Loaded };
}

/**
 * Marks the slice as failed, leaving any previously loaded properties in place.
 *
 * @param state
 */
export function setInputFieldPropertiesErrorReducer(
    state: Readonly<InputFieldPropertiesCollectionState>,
): Readonly<InputFieldPropertiesCollectionState> {
    return { ...state, status: LoadStatus.Error };
}

const createInputFieldPropertiesReducer = createReducer(
    initialInputFieldPropertiesCollectionState,
    on(initInputFieldProperties, setInputFieldPropertiesLoadingReducer),
    // Both the backend load and the storage rehydrate populate the slice identically; they are
    // distinct actions only so the persist effect fires for the backend load, not the rehydrate.
    on(inputFieldPropertiesLoaded, inputFieldPropertiesLoadedFromStorage, setInputFieldPropertiesLoadedReducer),
    on(inputFieldPropertiesFailed, setInputFieldPropertiesErrorReducer),
);

export function inputFieldPropertiesReducer(
    state: Readonly<InputFieldPropertiesCollectionState> | undefined,
    action: Action,
): Readonly<InputFieldPropertiesCollectionState> {
    return createInputFieldPropertiesReducer(state, action);
}
